import fs from "node:fs";
import path from "node:path";
import type { FileReport, LinterResult, LintReport, Offense, PolicyRule } from "../types.js";
import { execFile } from "../utils.js";
import { BaseLinter } from "./base.js";

interface RuffDiagnostic {
  code: string;
  message: string;
  filename: string;
  location: { row: number; column: number };
  end_location: { row: number; column: number };
  fix?: { message: string; applicability: string };
  url?: string;
}

export class RuffLinter extends BaseLinter {
  name = "ruff" as const;
  command = "ruff";
  installCmd = "pip install ruff";
  configFileName = "ruff.toml";

  createConfig(rules: PolicyRule[], tmpDir: string): string {
    const ruffRules = rules.filter((r) => r.linter === "ruff");
    if (ruffRules.length === 0) return "";

    const selectRules: string[] = [];
    const ignoreRules: string[] = [];

    for (const rule of ruffRules) {
      if (rule.status === "enabled") {
        selectRules.push(rule.slug);
      } else {
        ignoreRules.push(rule.slug);
      }
    }

    let content = `# Generated by Lint
line-length = 120
target-version = "py312"

[lint]
`;

    if (selectRules.length > 0) {
      content += `select = [${selectRules.map((r) => `"${r}"`).join(", ")}]\n`;
    }

    if (ignoreRules.length > 0) {
      content += `ignore = [${ignoreRules.map((r) => `"${r}"`).join(", ")}]\n`;
    }

    const configPath = path.join(tmpDir, this.configFileName);
    fs.writeFileSync(configPath, content, "utf-8");
    return configPath;
  }

  run(files: string[], configPath: string, autofix: boolean): LinterResult {
    let raw: string;
    try {
      raw = execFile(
        "ruff",
        [
          ...(autofix ? ["check", "--fix"] : ["check"]),
          ...(configPath ? [`--config=${configPath}`] : []),
          "--output-format=json",
          ...files,
        ],
        { silent: true },
      );
    } catch (error) {
      raw = (error as { stdout?: string }).stdout || "[]";
    }

    const report = this.parseOutput(raw, files);
    return { success: report.error_count === 0, report, raw };
  }

  parseOutput(raw: string, _files: string[]): LintReport {
    let diagnostics: RuffDiagnostic[];
    try {
      diagnostics = JSON.parse(raw);
    } catch {
      return this.emptyReport();
    }

    const fileMap = new Map<string, Offense[]>();

    for (const diag of diagnostics) {
      const offenses = fileMap.get(diag.filename) || [];
      offenses.push({
        rule: diag.code,
        message: diag.message,
        severity: diag.code.startsWith("E") || diag.code.startsWith("F") ? "error" : "warning",
        line: diag.location.row,
        column: diag.location.column,
        fixable: !!diag.fix,
      });
      fileMap.set(diag.filename, offenses);
    }

    const fileReports: FileReport[] = Array.from(fileMap.entries()).map(([filePath, offenses]) => ({
      path: filePath,
      offenses,
    }));

    return this.buildReport(fileReports);
  }
}
